import React, { useRef, useCallback, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler
);

const TestResults = ({ results, onRunAnother, onBackToProject, testId }) => {
    const dashboardRef = useRef(null);

    // Lazy import analytics to avoid circular deps
    const analyticsRef = useRef(null);
    useEffect(() => {
        import('../../utils/analytics').then(mod => {
            analyticsRef.current = mod;
            mod.trackTestResultsView(testId);
            mod.trackTestCompleted(results);
        });
    }, [testId, results]);

    const formatNumber = (num) => {
        if (num === undefined || num === null || isNaN(num)) return '0';
        return num.toLocaleString();
    };

    const formatMs = (ms) => {
        if (ms === undefined || ms === null || isNaN(ms)) return '0ms';
        return `${Math.round(ms)} ms`;
    };

    const formatMsShort = (ms) => {
        if (ms === undefined || ms === null || isNaN(ms)) return '0';
        if (ms < 1000) return `${Math.round(ms)}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    };

    const successRate = results.totalRequests > 0
        ? ((results.successfulRequests / results.totalRequests) * 100).toFixed(2)
        : '0.00';

    const errorRate = (results.errorPercentage || results.errorRate || 0).toFixed(2);

    const profileName = (results.loadProfile || 'fixed').charAt(0).toUpperCase() + (results.loadProfile || 'fixed').slice(1);
    const durationStr = results.testDuration ? `${results.testDuration}s` : 'N/A';
    const completedDate = results.completedAt
        ? new Date(results.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + ' at ' + new Date(results.completedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + ' at ' + new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    // --- Response Time Distribution Chart ---
    const latencyTimeline = results.latencyTimeline || [];
    const timelineLabels = latencyTimeline.map((_, i) => i);
    const timelineData = latencyTimeline.map(p => p.latency || p);
    const avgLine = results.averageResponseTime || 0;
    const p99Line = results.p99ResponseTime || 0;

    const distributionChartData = {
        labels: timelineLabels.length > 0 ? timelineLabels : [0, 1],
        datasets: [
            {
                label: 'Response Time',
                data: timelineData.length > 0 ? timelineData : [avgLine, avgLine],
                borderColor: '#3B6EF8',
                backgroundColor: 'rgba(59, 110, 248, 0.08)',
                borderWidth: 2,
                pointRadius: 0,
                tension: 0.3,
                fill: true,
                order: 3,
            },
            {
                label: `avg ${formatMsShort(avgLine)}`,
                data: timelineLabels.length > 0
                    ? timelineLabels.map(() => avgLine)
                    : [avgLine, avgLine],
                borderColor: '#D97706',
                borderWidth: 1.5,
                borderDash: [4, 3],
                pointRadius: 0,
                fill: false,
                order: 2,
            },
            {
                label: `p99 ${formatMsShort(p99Line)}`,
                data: timelineLabels.length > 0
                    ? timelineLabels.map(() => p99Line)
                    : [p99Line, p99Line],
                borderColor: '#EF4444',
                borderWidth: 1.5,
                borderDash: [5, 4],
                pointRadius: 0,
                fill: false,
                order: 1,
            },
        ],
    };

    const distributionOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    color: '#9CA3AF',
                    font: { size: 11, family: "'DM Sans', sans-serif" },
                    padding: 14,
                    usePointStyle: true,
                    pointStyle: 'line',
                },
            },
            tooltip: {
                backgroundColor: '#1F2937',
                titleColor: '#F9FAFB',
                bodyColor: '#9CA3AF',
                borderColor: '#E5E7EB',
                borderWidth: 1,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${Math.round(ctx.parsed.y)}ms`,
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: 'rgba(229, 231, 235, 0.5)' },
                ticks: { color: '#9CA3AF', font: { size: 10, family: "'DM Sans', sans-serif" } },
                title: { display: false },
            },
            x: {
                grid: { display: false },
                ticks: {
                    color: '#9CA3AF',
                    font: { size: 10, family: "'DM Sans', sans-serif" },
                    maxTicksLimit: 5,
                    callback: function (val, idx) {
                        const total = timelineLabels.length || 1;
                        if (idx === 0) return '0';
                        if (idx === total - 1) return formatNumber(results.totalRequests);
                        return Math.round((idx / total) * results.totalRequests);
                    },
                },
                title: {
                    display: true,
                    text: 'Request number',
                    color: '#9CA3AF',
                    font: { size: 10, family: "'DM Sans', sans-serif" },
                },
            },
        },
    };

    // --- HTTP Status Breakdown ---
    const statusCodes = results.statusCodeDistribution || {};
    const statusLabels = Object.keys(statusCodes);
    const statusValues = Object.values(statusCodes);

    // --- Export / Share ---
    const handleExportCSV = useCallback(() => {
        const rows = [
            ['Metric', 'Value'],
            ['Total Requests', results.totalRequests],
            ['Successful Requests', results.successfulRequests],
            ['Failed Requests', results.failedRequests],
            ['Requests/sec', results.requestsPerSecond],
            ['Avg Response Time (ms)', results.averageResponseTime],
            ['Min Response Time (ms)', results.minResponseTime],
            ['Max Response Time (ms)', results.maxResponseTime],
            ['Std Dev (ms)', results.stddevResponseTime || '~' + Math.round((results.maxResponseTime - results.minResponseTime) * 0.3)],
            ['P90 (ms)', results.p90ResponseTime],
            ['P95 (ms)', results.p95ResponseTime],
            ['P99 (ms)', results.p99ResponseTime],
            ['Error Rate (%)', errorRate],
            ['Success Rate (%)', successRate],
            ['Load Profile', results.loadProfile],
            ['Test Duration (s)', results.testDuration],
            ['Avg Concurrency', results.avgConcurrency || '~' + (results.totalRequests && results.requestsPerSecond ? Math.round(results.requestsPerSecond) : 'N/A')],
        ];
        // Add status code distribution
        rows.push([]);
        rows.push(['Status Code', 'Count']);
        const dist = results.statusCodeDistribution || {};
        for (const [code, count] of Object.entries(dist)) {
            rows.push([code, count]);
        }
        const csv = rows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `loadtest-results-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        if (analyticsRef.current) analyticsRef.current.trackExportCsv();
    }, [results, errorRate, successRate]);

    const handleExportJSON = useCallback(() => {
        const data = {
            ...results,
            exportedAt: new Date().toISOString(),
        };
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `loadtest-results-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        if (analyticsRef.current) analyticsRef.current.trackExportJson();
    }, [results]);

    const handleShare = useCallback(async () => {
        const shareText = `Load Test Results\n` +
            `Total Requests: ${formatNumber(results.totalRequests)}\n` +
            `Requests/sec: ${results.requestsPerSecond}\n` +
            `Avg Response: ${formatMsShort(results.averageResponseTime)}\n` +
            `Error Rate: ${errorRate}%\n` +
            `Success Rate: ${successRate}%\n` +
            `Profile: ${profileName} | Duration: ${durationStr}\n` +
            `\nPowered by APILoadTest.com`;

        if (navigator.share) {
            try {
                await navigator.share({ title: 'Load Test Results', text: shareText });
            } catch (e) {
                // User cancelled share
            }
        } else {
            try {
                await navigator.clipboard.writeText(shareText);
                alert('Results copied to clipboard!');
            } catch (e) {
                // Fallback
                const textarea = document.createElement('textarea');
                textarea.value = shareText;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                alert('Results copied to clipboard!');
            }
        }
        if (analyticsRef.current) analyticsRef.current.trackShareResults();
    }, [results, errorRate, successRate, profileName, durationStr]);

    // Status code description
    const statusDescription = (code) => {
        const c = parseInt(code);
        if (c === 200) return 'OK';
        if (c === 201) return 'Created';
        if (c === 204) return 'No Content';
        if (c === 301) return 'Moved';
        if (c === 302) return 'Found';
        if (c === 400) return 'Bad Request';
        if (c === 401) return 'Unauthorized';
        if (c === 403) return 'Forbidden';
        if (c === 404) return 'Not Found';
        if (c === 429) return 'Too Many Reqs';
        if (c === 500) return 'Server Error';
        if (c === 502) return 'Bad Gateway';
        if (c === 503) return 'Unavailable';
        return '';
    };

    const maxResponseForBar = results.maxResponseTime || 1;
    const maxPercentile = Math.max(
        results.p90ResponseTime || 0,
        results.p95ResponseTime || 0,
        results.p99ResponseTime || 0,
        1
    );

    // Compute stddev display
    const stddev = results.stddevResponseTime || Math.round((results.maxResponseTime - results.minResponseTime) * 0.3);

    // Pass ring calculations
    const passRingRadius = 38;
    const passRingCircumference = 2 * Math.PI * passRingRadius;
    const passRingOffset = results.totalRequests > 0
        ? passRingCircumference * (1 - results.successfulRequests / results.totalRequests)
        : passRingCircumference;

    // Status banner state
    const allPassed = parseFloat(errorRate) === 0;
    const statusBannerText = allPassed
        ? `All Systems Passed · ${successRate}% Success Rate`
        : `${successRate}% Success Rate · ${formatNumber(results.failedRequests)} failed`;

    return (
        <div className="fade-in" ref={dashboardRef}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

        .tr-dashboard {
          font-family: 'DM Sans', var(--font-body, sans-serif);
          background: #F9FAFB;
          color: #1F2937;
          border-radius: 16px;
          padding: 36px 24px 32px;
          min-height: 100vh;
        }

        /* HEADER */
        .tr-page-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 28px;
          animation: trFadeUp .4s ease both;
        }
        .tr-breadcrumb {
          display: flex; align-items: center; gap: 6px;
          font-size: 13px; color: #9CA3AF;
          margin-bottom: 6px;
        }
        .tr-breadcrumb a { color: #3B6EF8; text-decoration: none; cursor: pointer; }
        .tr-breadcrumb a:hover { text-decoration: underline; }
        .tr-page-title {
          font-size: 28px; font-weight: 700; letter-spacing: -.5px; color: #111827;
          margin: 0;
        }
        .tr-page-meta {
          display: flex; align-items: center; gap: 12px; margin-top: 8px; flex-wrap: wrap;
        }
        .tr-meta-tag {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 12px; font-weight: 500;
          padding: 3px 10px; border-radius: 20px;
          background: #EEF2FF; color: #3B6EF8;
        }
        .tr-meta-text { font-size: 13px; color: #9CA3AF; }
        .tr-header-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

        /* BUTTONS */
        .tr-btn-primary {
          background: #3B6EF8; color: #fff;
          border: none; border-radius: 8px;
          padding: 8px 18px; font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: inherit;
          transition: opacity .15s, transform .15s;
        }
        .tr-btn-primary:hover { opacity: .9; transform: translateY(-1px); }
        .tr-btn-ghost {
          background: transparent; border: 1.5px solid #E5E7EB;
          border-radius: 8px; padding: 7px 14px;
          font-size: 13px; font-weight: 500; color: #4B5563;
          cursor: pointer; font-family: inherit;
          transition: all .15s;
          display: flex; align-items: center; gap: 6px;
        }
        .tr-btn-ghost:hover {
          border-color: #3B6EF8; color: #3B6EF8; background: #EEF2FF;
        }

        /* STATUS BANNER */
        .tr-status-banner {
          background: linear-gradient(135deg, #fff 0%, #EEF2FF 100%);
          border: 1.5px solid #C7D7FD;
          border-radius: 16px;
          padding: 20px 28px;
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 24px;
          animation: trFadeUp .4s .05s ease both;
          flex-wrap: wrap;
          gap: 12px;
        }
        .tr-status-banner.error {
          background: linear-gradient(135deg, #fff 0%, #FEF2F2 100%);
          border-color: #FECACA;
        }
        .tr-status-left { display: flex; align-items: center; gap: 14px; }
        .tr-status-dot {
          width: 42px; height: 42px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .tr-status-dot.green { background: #DCFCE7; }
        .tr-status-dot.red { background: #FEE2E2; }
        .tr-status-label {
          font-size: 12px; color: #9CA3AF; font-weight: 500;
          letter-spacing: .5px; text-transform: uppercase;
        }
        .tr-status-value { font-size: 18px; font-weight: 700; color: #111827; margin-top: 1px; }
        .tr-status-right { font-size: 13px; color: #6B7280; }
        .tr-status-right strong { color: #1F2937; }

        /* KPI GRID */
        .tr-kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 24px;
          animation: trFadeUp .4s .1s ease both;
        }
        .tr-kpi-card {
          background: #fff;
          border: 1.5px solid #E5E7EB;
          border-radius: 16px;
          padding: 22px 24px;
          transition: box-shadow .2s, transform .2s;
          position: relative;
          overflow: hidden;
        }
        .tr-kpi-card:hover {
          box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.06);
          transform: translateY(-2px);
        }
        .tr-kpi-card::before {
          content: '';
          position: absolute; top: 0; left: 0; right: 0; height: 3px;
        }
        .tr-kpi-card.blue::before { background: #3B6EF8; }
        .tr-kpi-card.green::before { background: #16A34A; }
        .tr-kpi-card.amber::before { background: #D97706; }
        .tr-kpi-card.gray::before { background: #9CA3AF; }
        .tr-kpi-card.red::before { background: #DC2626; }

        .tr-kpi-icon {
          width: 36px; height: 36px;
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 14px;
          font-size: 16px;
        }
        .tr-kpi-icon.blue { background: #EEF2FF; }
        .tr-kpi-icon.green { background: #DCFCE7; }
        .tr-kpi-icon.amber { background: #FEF3C7; }
        .tr-kpi-icon.red { background: #FEE2E2; }

        .tr-kpi-label {
          font-size: 11px; font-weight: 600; color: #9CA3AF;
          letter-spacing: .6px; text-transform: uppercase; margin-bottom: 6px;
        }
        .tr-kpi-value {
          font-size: 32px; font-weight: 700; letter-spacing: -1px;
          color: #111827; line-height: 1;
        }
        .tr-kpi-unit {
          font-size: 15px; font-weight: 500; color: #9CA3AF; margin-left: 2px;
        }
        .tr-kpi-sub { font-size: 12px; color: #9CA3AF; margin-top: 6px; }

        /* CARDS */
        .tr-card {
          background: #fff;
          border: 1.5px solid #E5E7EB;
          border-radius: 16px;
          padding: 24px;
        }
        .tr-card-title {
          font-size: 14px; font-weight: 700; color: #111827;
          margin-bottom: 20px;
          display: flex; align-items: center; gap: 8px;
        }
        .tr-card-title-icon {
          width: 28px; height: 28px;
          background: #EEF2FF;
          border-radius: 7px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .tr-card-title-subtitle {
          font-size: 11px; color: #9CA3AF; font-weight: 400; margin-left: auto;
        }

        /* GRIDS */
        .tr-main-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
          animation: trFadeUp .4s .18s ease both;
        }
        .tr-bottom-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 16px;
          animation: trFadeUp .4s .24s ease both;
        }

        /* RESPONSE TIMES */
        .tr-rt-row { margin-bottom: 16px; }
        .tr-rt-row:last-child { margin-bottom: 0; }
        .tr-rt-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .tr-rt-name { font-size: 13px; color: #4B5563; font-weight: 500; }
        .tr-rt-val {
          font-size: 13px; font-weight: 700; color: #111827;
          font-family: 'DM Mono', monospace;
        }
        .tr-rt-bar { height: 6px; background: #F3F4F6; border-radius: 99px; overflow: hidden; }
        .tr-rt-fill {
          height: 100%; border-radius: 99px;
          animation: trFillBar .9s cubic-bezier(.4,0,.2,1) both;
        }

        /* PERCENTILE TABLE */
        .tr-pct-row {
          display: flex; align-items: center;
          padding: 12px 14px;
          border-radius: 10px;
          margin-bottom: 6px;
          background: #F9FAFB;
        }
        .tr-pct-label {
          font-size: 12px; font-weight: 700; letter-spacing: .5px;
          color: #fff; background: #3B6EF8;
          padding: 2px 8px; border-radius: 5px;
          margin-right: 14px; min-width: 36px; text-align: center;
        }
        .tr-pct-bar-wrap {
          flex: 1; height: 6px; background: #E5E7EB;
          border-radius: 99px; overflow: hidden; margin-right: 14px;
        }
        .tr-pct-fill {
          height: 100%; border-radius: 99px;
          animation: trFillBar .9s cubic-bezier(.4,0,.2,1) both;
        }
        .tr-pct-val {
          font-size: 13px; font-weight: 700; font-family: 'DM Mono', monospace;
          color: #1F2937; min-width: 60px; text-align: right;
        }

        /* REQUEST SUMMARY */
        .tr-req-summary-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #F3F4F6;
        }
        .tr-req-summary-row:last-child { border-bottom: none; }
        .tr-req-summary-label {
          font-size: 13px; color: #4B5563;
          display: flex; align-items: center; gap: 8px;
        }
        .tr-req-dot { width: 8px; height: 8px; border-radius: 50%; }
        .tr-req-summary-val {
          font-size: 14px; font-weight: 700; font-family: 'DM Mono', monospace;
        }

        /* HTTP STATUS */
        .tr-http-block {
          border: 1.5px solid #BBF7D0;
          border-radius: 12px;
          padding: 18px 20px;
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 12px;
        }
        .tr-http-block.success { background: #DCFCE7; }
        .tr-http-block.error { background: #FEE2E2; border-color: #FECACA; }
        .tr-http-block.warning { background: #FEF3C7; border-color: #FDE68A; }
        .tr-http-code {
          font-size: 20px; font-weight: 800;
          font-family: 'DM Mono', monospace;
        }
        .tr-http-desc { font-size: 13px; font-weight: 500; }
        .tr-http-badge {
          font-size: 12px; font-weight: 700; padding: 4px 12px;
          border-radius: 20px; color: #fff;
        }

        /* PASS RING */
        .tr-pass-ring-wrap {
          display: flex; flex-direction: column; align-items: center;
          padding-top: 8px;
        }
        .tr-pass-ring { position: relative; width: 90px; height: 90px; margin: 0 auto 12px; }
        .tr-pass-ring svg { transform: rotate(-90deg); }
        .tr-pass-ring-center {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
        }
        .tr-pass-ring-num { font-size: 20px; font-weight: 800; color: #16A34A; line-height: 1; }
        .tr-pass-ring-denom { font-size: 10px; color: #9CA3AF; font-weight: 500; }
        .tr-pass-label { font-size: 12px; font-weight: 600; color: #16A34A; text-align: center; }
        .tr-pass-sub { font-size: 11px; color: #9CA3AF; margin-top: 2px; text-align: center; }

        /* CHART */
        .tr-chart-area {
          height: 220px;
          position: relative;
        }

        /* ERRORS */
        .tr-errors-card {
          margin-bottom: 16px;
          animation: trFadeUp .4s .28s ease both;
        }

        /* FOOTER */
        .tr-result-footer {
          margin-top: 28px;
          display: flex; align-items: center; justify-content: space-between;
          animation: trFadeUp .4s .3s ease both;
          flex-wrap: wrap;
          gap: 12px;
        }
        .tr-test-id {
          font-size: 12px; color: #9CA3AF;
          font-family: 'DM Mono', monospace;
        }
        .tr-footer-actions { display: flex; gap: 8px; flex-wrap: wrap; }

        @keyframes trFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes trFillBar {
          from { width: 0; }
        }

        /* SVG icons */
        .tr-icon-svg { display: inline-block; vertical-align: middle; }

        /* Responsive */
        @media (max-width: 900px) {
          .tr-kpi-grid { grid-template-columns: 1fr 1fr; }
          .tr-main-grid, .tr-bottom-grid { grid-template-columns: 1fr; }
          .tr-page-header { flex-direction: column; gap: 16px; }
          .tr-status-banner { flex-direction: column; text-align: center; }
        }
        @media (max-width: 600px) {
          .tr-dashboard { padding: 20px 12px 24px; }
          .tr-page-title { font-size: 22px; }
          .tr-kpi-value { font-size: 24px; }
          .tr-kpi-grid { gap: 10px; }
          .tr-header-actions { flex-direction: column; width: 100%; }
          .tr-result-footer { flex-direction: column; text-align: center; }
        }
      `}</style>

            <div className="tr-dashboard">
                {/* Page Header */}
                <div className="tr-page-header">
                    <div>
                        <div className="tr-breadcrumb">
                            <a href="#projects" onClick={(e) => { e.preventDefault(); onBackToProject(); }}>Projects</a>
                            <span>›</span>
                            <span>{profileName} Profile</span>
                            <span>›</span>
                            <span style={{ color: '#4B5563' }}>Test Results</span>
                        </div>
                        <h1 className="tr-page-title">Test Results</h1>
                        <div className="tr-page-meta">
                            <span className="tr-meta-tag">
                                <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" /><path d="M6 3.5V6l1.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                                {profileName} · {durationStr} duration
                            </span>
                            <span className="tr-meta-text">{completedDate}</span>
                            {testId && <span className="tr-meta-text">ID: #{typeof testId === 'string' ? testId.substring(0, 8) : testId}</span>}
                        </div>
                    </div>
                    <div className="tr-header-actions">
                        <button className="tr-btn-ghost" onClick={handleExportCSV}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                            Export CSV
                        </button>
                        <button className="tr-btn-ghost" onClick={handleShare}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
                            Share
                        </button>
                        <button className="tr-btn-primary" onClick={() => { if (analyticsRef.current) analyticsRef.current.trackRunAnotherTestClick(); onRunAnother(); }}>
                            Run Another Test
                        </button>
                    </div>
                </div>

                {/* Status Banner */}
                <div className={`tr-status-banner${allPassed ? '' : ' error'}`}>
                    <div className="tr-status-left">
                        <div className={`tr-status-dot ${allPassed ? 'green' : 'red'}`}>
                            {allPassed ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            )}
                        </div>
                        <div>
                            <div className="tr-status-label">Test Status</div>
                            <div className="tr-status-value">{statusBannerText}</div>
                        </div>
                    </div>
                    <div className="tr-status-right">
                        <strong>{formatNumber(results.successfulRequests)} / {formatNumber(results.totalRequests)}</strong> requests completed
                        {allPassed ? ' · Zero errors detected' : ` · ${formatNumber(results.failedRequests)} errors`}
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="tr-kpi-grid">
                    <div className="tr-kpi-card blue">
                        <div className="tr-kpi-icon blue">📦</div>
                        <div className="tr-kpi-label">Total Requests</div>
                        <div className="tr-kpi-value">
                            {formatNumber(results.totalRequests)}
                            <span className="tr-kpi-unit">reqs</span>
                        </div>
                        <div className="tr-kpi-sub">Over {durationStr} window</div>
                    </div>
                    <div className="tr-kpi-card blue">
                        <div className="tr-kpi-icon blue">⚡</div>
                        <div className="tr-kpi-label">Throughput</div>
                        <div className="tr-kpi-value">
                            {(results.requestsPerSecond || 0).toFixed(2)}
                            <span className="tr-kpi-unit">rps</span>
                        </div>
                        <div className="tr-kpi-sub">Requests per second</div>
                    </div>
                    <div className="tr-kpi-card amber">
                        <div className="tr-kpi-icon amber">⏱</div>
                        <div className="tr-kpi-label">Avg Response Time</div>
                        <div className="tr-kpi-value">
                            {Math.round(results.averageResponseTime || 0)}
                            <span className="tr-kpi-unit">ms</span>
                        </div>
                        <div className="tr-kpi-sub">{Math.round(results.minResponseTime || 0)}ms min · {Math.round(results.maxResponseTime || 0)}ms max</div>
                    </div>
                    <div className={`tr-kpi-card ${parseFloat(errorRate) > 0 ? 'red' : 'green'}`}>
                        <div className={`tr-kpi-icon ${parseFloat(errorRate) > 0 ? 'red' : 'green'}`}>
                            {parseFloat(errorRate) > 0 ? '⚠️' : '✅'}
                        </div>
                        <div className="tr-kpi-label">Error Rate</div>
                        <div className="tr-kpi-value">
                            {errorRate}
                            <span className="tr-kpi-unit">%</span>
                        </div>
                        <div className="tr-kpi-sub">
                            {parseFloat(errorRate) > 0
                                ? `${formatNumber(results.failedRequests)} failed requests`
                                : 'No failed requests'}
                        </div>
                    </div>
                </div>

                {/* Main Grid: Response Times + Percentiles */}
                <div className="tr-main-grid">
                    {/* Response Times */}
                    <div className="tr-card">
                        <div className="tr-card-title">
                            <div className="tr-card-title-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B6EF8" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                            </div>
                            Response Times
                        </div>
                        <div className="tr-rt-row">
                            <div className="tr-rt-header">
                                <span className="tr-rt-name">Minimum</span>
                                <span className="tr-rt-val" style={{ color: '#3B6EF8' }}>{formatMs(results.minResponseTime)}</span>
                            </div>
                            <div className="tr-rt-bar">
                                <div className="tr-rt-fill" style={{
                                    width: `${Math.max((results.minResponseTime / maxResponseForBar) * 100, 3)}%`,
                                    background: '#3B6EF8',
                                    animationDelay: '.1s'
                                }} />
                            </div>
                        </div>
                        <div className="tr-rt-row">
                            <div className="tr-rt-header">
                                <span className="tr-rt-name">Average</span>
                                <span className="tr-rt-val" style={{ color: '#D97706' }}>{formatMs(results.averageResponseTime)}</span>
                            </div>
                            <div className="tr-rt-bar">
                                <div className="tr-rt-fill" style={{
                                    width: `${Math.max((results.averageResponseTime / maxResponseForBar) * 100, 3)}%`,
                                    background: '#D97706',
                                    animationDelay: '.2s'
                                }} />
                            </div>
                        </div>
                        <div className="tr-rt-row">
                            <div className="tr-rt-header">
                                <span className="tr-rt-name">Maximum</span>
                                <span className="tr-rt-val" style={{ color: '#EF4444' }}>{formatMs(results.maxResponseTime)}</span>
                            </div>
                            <div className="tr-rt-bar">
                                <div className="tr-rt-fill" style={{
                                    width: '100%',
                                    background: '#EF4444',
                                    animationDelay: '.3s'
                                }} />
                            </div>
                        </div>
                        <div className="tr-rt-row" style={{ marginBottom: 0 }}>
                            <div className="tr-rt-header">
                                <span className="tr-rt-name">Std Deviation</span>
                                <span className="tr-rt-val" style={{ color: '#9CA3AF' }}>~{Math.round(stddev)} ms</span>
                            </div>
                            <div className="tr-rt-bar">
                                <div className="tr-rt-fill" style={{
                                    width: `${Math.max((stddev / maxResponseForBar) * 100, 3)}%`,
                                    background: '#D1D5DB',
                                    animationDelay: '.4s'
                                }} />
                            </div>
                        </div>
                    </div>

                    {/* Percentile Response Times */}
                    <div className="tr-card">
                        <div className="tr-card-title">
                            <div className="tr-card-title-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B6EF8" strokeWidth="2.5"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /></svg>
                            </div>
                            Percentile Response Times
                        </div>
                        <div className="tr-pct-row">
                            <div className="tr-pct-label">P90</div>
                            <div className="tr-pct-bar-wrap">
                                <div className="tr-pct-fill" style={{
                                    width: `${Math.max(((results.p90ResponseTime || results.averageResponseTime * 1.5) / maxPercentile) * 100, 3)}%`,
                                    background: '#3B6EF8',
                                    animationDelay: '.1s'
                                }} />
                            </div>
                            <div className="tr-pct-val" style={{ color: '#3B6EF8' }}>
                                {formatMs(results.p90ResponseTime || results.averageResponseTime * 1.5)}
                            </div>
                        </div>
                        <div className="tr-pct-row">
                            <div className="tr-pct-label" style={{ background: '#D97706' }}>P95</div>
                            <div className="tr-pct-bar-wrap">
                                <div className="tr-pct-fill" style={{
                                    width: `${Math.max(((results.p95ResponseTime || results.averageResponseTime * 1.8) / maxPercentile) * 100, 3)}%`,
                                    background: '#D97706',
                                    animationDelay: '.2s'
                                }} />
                            </div>
                            <div className="tr-pct-val" style={{ color: '#D97706' }}>
                                {formatMs(results.p95ResponseTime || results.averageResponseTime * 1.8)}
                            </div>
                        </div>
                        <div className="tr-pct-row">
                            <div className="tr-pct-label" style={{ background: '#EF4444' }}>P99</div>
                            <div className="tr-pct-bar-wrap">
                                <div className="tr-pct-fill" style={{
                                    width: `${Math.max(((results.p99ResponseTime || results.averageResponseTime * 2.2) / maxPercentile) * 100, 3)}%`,
                                    background: '#EF4444',
                                    animationDelay: '.3s'
                                }} />
                            </div>
                            <div className="tr-pct-val" style={{ color: '#EF4444' }}>
                                {formatMs(results.p99ResponseTime || results.averageResponseTime * 2.2)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Grid: Distribution Chart + Request Summary + HTTP Status */}
                <div className="tr-bottom-grid">
                    {/* Response Time Distribution */}
                    <div className="tr-card">
                        <div className="tr-card-title">
                            <div className="tr-card-title-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B6EF8" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                            </div>
                            Response Time Distribution
                            <span className="tr-card-title-subtitle">Latency over request sequence (ms)</span>
                        </div>
                        <div className="tr-chart-area">
                            <Line data={distributionChartData} options={distributionOptions} />
                        </div>
                    </div>

                    {/* Right column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Request Summary */}
                        <div className="tr-card" style={{ flex: 1 }}>
                            <div className="tr-card-title">
                                <div className="tr-card-title-icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B6EF8" strokeWidth="2.5"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>
                                </div>
                                Request Summary
                            </div>
                            <div className="tr-req-summary-row">
                                <span className="tr-req-summary-label">
                                    <div className="tr-req-dot" style={{ background: '#16A34A' }} />
                                    Successful
                                </span>
                                <span className="tr-req-summary-val" style={{ color: '#16A34A' }}>
                                    {formatNumber(results.successfulRequests)}
                                </span>
                            </div>
                            <div className="tr-req-summary-row">
                                <span className="tr-req-summary-label">
                                    <div className="tr-req-dot" style={{ background: '#EF4444' }} />
                                    Failed
                                </span>
                                <span className="tr-req-summary-val" style={{ color: '#EF4444' }}>
                                    {formatNumber(results.failedRequests)}
                                </span>
                            </div>
                            <div className="tr-req-summary-row">
                                <span className="tr-req-summary-label">Success Rate</span>
                                <span className="tr-req-summary-val" style={{ color: parseFloat(successRate) >= 99 ? '#16A34A' : '#D97706' }}>
                                    {successRate}%
                                </span>
                            </div>
                            <div className="tr-req-summary-row">
                                <span className="tr-req-summary-label">Avg Concurrency</span>
                                <span className="tr-req-summary-val" style={{ color: '#1F2937' }}>
                                    ~{Math.round(results.avgConcurrency || results.totalRequests / (results.testDuration || 1))} users
                                </span>
                            </div>
                        </div>

                        {/* HTTP Status */}
                        <div className="tr-card">
                            <div className="tr-card-title">
                                <div className="tr-card-title-icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B6EF8" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>
                                </div>
                                HTTP Status
                            </div>
                            {statusLabels.map((code, i) => {
                                const count = statusValues[i];
                                const c = parseInt(code);
                                const isSuccess = c >= 200 && c < 300;
                                const isError = c >= 400;
                                const blockClass = isSuccess ? 'success' : isError ? 'error' : 'warning';
                                const codeColor = isSuccess ? '#16A34A' : isError ? '#DC2626' : '#D97706';
                                return (
                                    <div key={code} className={`tr-http-block ${blockClass}`}>
                                        <div>
                                            <div className="tr-http-code" style={{ color: codeColor }}>
                                                {code} {statusDescription(code)}
                                            </div>
                                            <div className="tr-http-desc" style={{ color: codeColor }}>
                                                {statusLabels.length === 1 ? 'All requests' : `${((count / results.totalRequests) * 100).toFixed(0)}% of requests`}
                                            </div>
                                        </div>
                                        <div className="tr-http-badge" style={{ background: codeColor }}>
                                            {formatNumber(count)} reqs
                                        </div>
                                    </div>
                                );
                            })}

                            <div className="tr-pass-ring-wrap">
                                <div className="tr-pass-ring">
                                    <svg width="90" height="90" viewBox="0 0 90 90">
                                        <circle cx="45" cy="45" r={passRingRadius} fill="none" stroke="#DCFCE7" strokeWidth="8" />
                                        <circle cx="45" cy="45" r={passRingRadius} fill="none"
                                            stroke="#16A34A" strokeWidth="8"
                                            strokeDasharray={passRingCircumference}
                                            strokeDashoffset={passRingOffset}
                                            strokeLinecap="round" />
                                    </svg>
                                    <div className="tr-pass-ring-center">
                                        <div className="tr-pass-ring-num">{formatNumber(results.successfulRequests)}</div>
                                        <div className="tr-pass-ring-denom">/ {formatNumber(results.totalRequests)}</div>
                                    </div>
                                </div>
                                <div className="tr-pass-label">✓ Successful</div>
                                <div className="tr-pass-sub">{successRate}% pass rate</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Errors Section */}
                {results.errors && results.errors.length > 0 && (
                    <div className="tr-card tr-errors-card">
                        <div className="tr-card-title" style={{ color: '#DC2626' }}>
                            <div className="tr-card-title-icon" style={{ background: '#FEE2E2' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                            </div>
                            Errors
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {results.errors.slice(0, 10).map((error, index) => (
                                <div key={index} style={{
                                    padding: '10px 14px',
                                    background: '#FEF2F2',
                                    border: '1px solid #FECACA',
                                    borderRadius: '8px',
                                    marginBottom: '6px',
                                    fontSize: '13px',
                                    color: '#DC2626',
                                    fontFamily: "'DM Mono', monospace",
                                    wordBreak: 'break-all',
                                }}>
                                    {error}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="tr-result-footer">
                    <div>
                        <span className="tr-test-id">
                            {testId && `Test ID: #${testId} · `}Profile: {profileName}
                        </span>
                    </div>
                    <div className="tr-footer-actions">
                        <button className="tr-btn-ghost" onClick={() => { if (analyticsRef.current) analyticsRef.current.trackBackToProjectClick(); onBackToProject(); }}>
                            ← Back to Project
                        </button>
                        <button className="tr-btn-ghost" onClick={handleExportJSON}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                            Export JSON
                        </button>
                        <button className="tr-btn-primary" onClick={() => { if (analyticsRef.current) analyticsRef.current.trackRunAnotherTestClick(); onRunAnother(); }}>
                            Run Another Test →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TestResults;
